import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Plus,
  Search,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  Loader2,
  Target,
  AlertTriangle
} from 'lucide-react';
import { risksApi, assetsApi } from '../lib/api';
import { toast } from 'sonner';
import { cn, formatDate } from '../lib/utils';

const riskLevels = [
  { value: 'critica', label: 'Crítico', color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'alta', label: 'Alto', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'media', label: 'Médio', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  { value: 'baixa', label: 'Baixo', color: 'bg-green-50 text-green-700 border-green-200' },
];

const riskStatuses = [
  { value: 'identificado', label: 'Identificado' },
  { value: 'em_analise', label: 'Em Análise' },
  { value: 'em_mitigacao', label: 'Em Mitigação' },
  { value: 'mitigado', label: 'Mitigado' },
  { value: 'aceite', label: 'Aceite' },
  { value: 'fechado', label: 'Fechado' },
];

const riskCategories = [
  { value: 'operacional', label: 'Operacional' },
  { value: 'tecnologico', label: 'Tecnológico' },
  { value: 'ciberseguranca', label: 'Cibersegurança' },
  { value: 'conformidade', label: 'Conformidade' },
  { value: 'reputacional', label: 'Reputacional' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'cadeia_abastecimento', label: 'Cadeia de Abastecimento' },
  { value: 'recursos_humanos', label: 'Recursos Humanos' },
];

const probabilityImpactLevels = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Crítica' },
];

const initialFormData = {
  title: '',
  description: '',
  category: 'ciberseguranca',
  probability: 'media',
  impact: 'media',
  asset_ids: [],
  mitigation_measures: '',
};

export default function Risks() {
  const [risks, setRisks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [levelFilter, statusFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (levelFilter) params.risk_level = levelFilter;
      if (statusFilter) params.status = statusFilter;
      
      const [risksRes, assetsRes] = await Promise.all([
        risksApi.getAll(params),
        assetsApi.getAll({}),
      ]);
      
      setRisks(risksRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('O título é obrigatório');
      return;
    }

    setIsSaving(true);
    try {
      await risksApi.create(formData);
      toast.success('Risco registado com sucesso');
      setShowForm(false);
      setFormData(initialFormData);
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar risco');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredRisks = risks.filter(
    (risk) =>
      risk.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      risk.risk_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = risks.reduce(
    (acc, risk) => {
      acc.total++;
      if (risk.risk_level === 'critica') acc.critical++;
      if (risk.risk_level === 'alta') acc.high++;
      if (risk.status === 'mitigado' || risk.status === 'fechado') acc.mitigated++;
      return acc;
    },
    { total: 0, critical: 0, high: 0, mitigated: 0 }
  );

  const getLevelConfig = (level) => riskLevels.find(l => l.value === level) || riskLevels[2];
  const getCategoryLabel = (cat) => riskCategories.find(c => c.value === cat)?.label || cat;
  const getStatusLabel = (st) => riskStatuses.find(s => s.value === st)?.label || st;

  // Calculate risk matrix position
  const getRiskScore = (probability, impact) => {
    const levels = { baixa: 1, media: 2, alta: 3, critica: 4 };
    return levels[probability] * levels[impact];
  };

  const headerContent = (
    <Button onClick={() => { setFormData(initialFormData); setShowForm(true); }} data-testid="new-risk-btn">
      <Plus className="h-4 w-4 mr-2" />
      Registar Risco
    </Button>
  );

  if (isLoading) {
    return (
      <Layout title="Gestão de Riscos">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestão de Riscos" headerContent={headerContent}>
      <div className="space-y-6" data-testid="risks-container">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-flat">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <ShieldAlert className="h-8 w-8 text-slate-400" />
                <span className="text-2xl font-bold text-slate-900">{stats.total}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Total de Riscos</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="h-8 w-8 text-red-500" />
                <span className="text-2xl font-bold text-red-600">{stats.critical}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Críticos</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <TrendingUp className="h-8 w-8 text-orange-500" />
                <span className="text-2xl font-bold text-orange-600">{stats.high}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Altos</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <TrendingDown className="h-8 w-8 text-green-500" />
                <span className="text-2xl font-bold text-green-600">{stats.mitigated}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Mitigados</p>
            </CardContent>
          </Card>
        </div>

        {/* Risk Matrix */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />
              Matriz de Riscos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="grid grid-cols-5 gap-1 min-w-[400px]">
                <div className="text-xs text-slate-500 text-center p-2"></div>
                {['Baixa', 'Média', 'Alta', 'Crítica'].map((label) => (
                  <div key={label} className="text-xs font-medium text-slate-600 text-center p-2">{label}</div>
                ))}
                {['Crítica', 'Alta', 'Média', 'Baixa'].map((probLabel, probIdx) => (
                  <>
                    <div key={`label-${probLabel}`} className="text-xs font-medium text-slate-600 text-center p-2 flex items-center justify-center">
                      {probLabel}
                    </div>
                    {[1, 2, 3, 4].map((impactLevel) => {
                      const prob = ['baixa', 'media', 'alta', 'critica'][3 - probIdx];
                      const impact = ['baixa', 'media', 'alta', 'critica'][impactLevel - 1];
                      const score = (4 - probIdx) * impactLevel;
                      const risksInCell = risks.filter(r => r.probability === prob && r.impact === impact);
                      
                      let bgColor = 'bg-green-100';
                      if (score >= 12) bgColor = 'bg-red-200';
                      else if (score >= 6) bgColor = 'bg-orange-200';
                      else if (score >= 3) bgColor = 'bg-yellow-200';
                      
                      return (
                        <div key={`${prob}-${impact}`} className={cn('h-16 rounded flex items-center justify-center text-xs font-medium', bgColor)}>
                          {risksInCell.length > 0 && (
                            <span className="bg-slate-900 text-white rounded-full h-6 w-6 flex items-center justify-center">
                              {risksInCell.length}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </>
                ))}
              </div>
              <div className="flex justify-center gap-4 mt-4 text-xs">
                <span className="text-slate-500">Impacto →</span>
                <span className="text-slate-500">| Probabilidade ↑</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="card-flat">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar riscos..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-risks-input"
                />
              </div>
              <Select value={levelFilter} onValueChange={(v) => setLevelFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Nível" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {riskLevels.map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {riskStatuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Risks List */}
        {filteredRisks.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="p-8 text-center">
              <ShieldAlert className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Nenhum risco encontrado</h3>
              <p className="text-sm text-slate-500 mb-4">
                {searchQuery || levelFilter || statusFilter ? 'Tente ajustar os filtros' : 'Não há riscos registados'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filteredRisks.map((risk) => {
              const levelConfig = getLevelConfig(risk.risk_level);
              const score = getRiskScore(risk.probability, risk.impact);
              
              return (
                <Card key={risk.risk_id} className="card-flat hover:shadow-sm transition-shadow" data-testid={`risk-${risk.risk_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={cn('h-12 w-12 rounded-lg flex items-center justify-center', levelConfig.color)}>
                        <ShieldAlert className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-slate-400">{risk.risk_id}</span>
                              <Badge variant="outline" className={cn('text-xs', levelConfig.color)}>
                                {levelConfig.label}
                              </Badge>
                            </div>
                            <h3 className="font-medium text-slate-900">{risk.title}</h3>
                            <p className="text-sm text-slate-500">{getCategoryLabel(risk.category)}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-slate-700">{score}</div>
                            <div className="text-xs text-slate-400">Score</div>
                          </div>
                        </div>
                        {risk.description && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-2">{risk.description}</p>
                        )}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>Prob: {risk.probability}</span>
                            <span>Impacto: {risk.impact}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {getStatusLabel(risk.status)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Risk Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registar Risco</DialogTitle>
            <DialogDescription>Registe um novo risco identificado</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Risco de ataque ransomware"
                required
                data-testid="risk-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição detalhada do risco..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {riskCategories.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="probability">Probabilidade</Label>
                <Select value={formData.probability} onValueChange={(v) => setFormData({ ...formData, probability: v })}>
                  <SelectTrigger id="probability">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {probabilityImpactLevels.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="impact">Impacto</Label>
                <Select value={formData.impact} onValueChange={(v) => setFormData({ ...formData, impact: v })}>
                  <SelectTrigger id="impact">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {probabilityImpactLevels.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mitigation">Medidas de Mitigação</Label>
              <Textarea
                id="mitigation"
                value={formData.mitigation_measures}
                onChange={(e) => setFormData({ ...formData, mitigation_measures: e.target.value })}
                placeholder="Descreva as medidas de mitigação propostas..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSaving} data-testid="save-risk-btn">
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Registar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
